import { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Alert, Box } from '@mui/material';
import { useAuth } from '../context/authContext';

const CHANGE_PASSWORD_PATH = '/change-password';

export const ProtectedRoute = ({ children }: { children: ReactNode }) => {
  const { isAuthenticated, user } = useAuth();
  const location = useLocation();

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Force a password change (e.g. after an admin reset) before letting the
  // user reach anything else — every authenticated route renders through
  // this one component, so this is a single-point enforcement rather than
  // a per-route guard.
  if (user?.mustChangePassword && location.pathname !== CHANGE_PASSWORD_PATH) {
    return <Navigate to={CHANGE_PASSWORD_PATH} replace />;
  }

  return <>{children}</>;
};

interface RequirePermissionProps {
  module: string;
  action: string;
  children: ReactNode;
  /** Phase 76: an explicit role-based block, layered on top of the
   * ordinary permission check — for Administration routes (Users,
   * Branches, Master Data) where the role still legitimately holds the
   * underlying permission for non-Administration uses elsewhere (e.g. a
   * Counselor-picker dropdown), but must never reach the admin page
   * itself. Roles/Audit Logs don't need this — Branch Manager holds no
   * permission for either any more, so the ordinary check already blocks
   * them (see permissions.ts). */
  blockRoles?: string[];
  /** Only these roles may open the page (checked in addition to the
   * permission). */
  allowRoles?: string[];
}

export const RequirePermission = ({ module, action, children, blockRoles, allowRoles }: RequirePermissionProps) => {
  const { hasPermission, user } = useAuth();

  const blocked =
    !hasPermission(module, action) ||
    (!!user && !!blockRoles?.includes(user.role.name)) ||
    (!!allowRoles && (!user || !allowRoles.includes(user.role.name)));

  if (blocked) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">
          You don&apos;t have permission to view this page ({module}:{action}).
        </Alert>
      </Box>
    );
  }

  return <>{children}</>;
};
